import * as React from 'react';
import { useElementWidth, useFluidGrid } from '@deltasierra/react-hooks';

export type FluidGridProps<T> = {
    dataCy?: string;
    getDimensions: (item: T) => Record<'height' | 'width', number>;
    itemMinWidth: number;
    items: ReadonlyArray<T>;
    render: (
        props: React.HTMLAttributes<any> & { key: any },
        item: T,
        dimensions: Record<'height' | 'width', number>,
        index: number,
    ) => React.ReactNode;
    rowMaxHeight: number;
    rowMinHeight: number;
    /**
     * @default 10
     */
    spacing?: number;
};

export const FluidGrid = <T, >(props: FluidGridProps<T>): React.ReactElement<FluidGridProps<T>> => {
    const { ref, width } = useElementWidth<HTMLDivElement>();
    const spacing = props.spacing ?? 10;
    const itemSizes = useFluidGrid({
        itemMinWidth: props.itemMinWidth,
        items: props.items.map(item => props.getDimensions(item)),
        rowMaxHeight: props.rowMaxHeight,
        rowMinHeight: props.rowMinHeight,
        rowWidth: width,
        spacing,
    });

    return (
        <div
            data-cy={props.dataCy}
            ref={ref}
            style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
            }}
        >
            {itemSizes.map((dimensions, index) =>
                props.render(
                    {
                        key: index,
                        style: {
                            height: dimensions.height,
                            marginBottom: spacing,
                            width: dimensions.width,
                        },
                    },
                    props.items[index],
                    dimensions,
                    index,
                ),
            )}
        </div>
    );
};
FluidGrid.displayName = 'FluidGrid';
